// components/organization/ExamsTable.jsx
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { httpRequest } from "../../utils/httpRequest";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

const stripHtml = (s = "") => String(s).replace(/<[^>]*>/g, "");

const fmtMinutes = (min) => {
  const n = Number(min) || 0;
  if (n < 60) return `${n}m`;
  const h = Math.floor(n / 60);
  const m = n % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
};

const fmtDate = (iso) => {
  try {
    const d = new Date(iso);
    return isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
  } catch {
    return "—";
  }
};

export default function ExamsTable({
  exams = [],
  onManage,
  onView,
  handleUnpublish,
  mayCreate,
}) {
  const { i18n, t } = useTranslation();
  const isRtl = i18n.language.includes("fa");
  const navigate = useNavigate();

  const rows = useMemo(
    () =>
      exams.map((ex) => ({
        id: ex._id,
        title: ex?.title?.fa || ex?.title?.en || "-",
        category: ex?.category?.title || "-",
        level: ex?.category?.level ? `L${ex.category.level}` : "",
        time: fmtMinutes(ex?.totalTimeMin),
        questions: Number(ex?.itemsCount || 0),
        sections: Number(ex?.sectionCount || 0),
        status: ex?.isPublished
          ? t("org_exams.status_published") || "Published"
          : t("org_exams.status_draft") || "Draft",
        statusClass: ex?.isPublished
          ? "bg-green-100 text-green-700"
          : "bg-amber-100 text-amber-700",
        updatedAt: fmtDate(ex?.updatedAt || ex?.createdAt),
      })),
    [exams, t]
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border mt-6 overflow-x-auto">
      <table
        className={`min-w-full text-sm ${isRtl ? "text-right" : "text-left"}`}
      >
        <thead className="bg-gray-50 text-gray-700">
          <tr>
            <th className="py-2 px-4 border-b">
              {t("org_exams.table_title") || "Title"}
            </th>
            <th className="py-2 px-4 border-b">
              {t("org_exams.table_category") || "Category"}
            </th>
            <th className="py-2 px-4 border-b">
              {t("org_exams.table_time") || "Time"}
            </th>
            <th className="py-2 px-4 border-b">
              {t("org_exams.table_questions") || "Questions"}
            </th>
            <th className="py-2 px-4 border-b">
              {t("org_exams.table_sections") || "Sections"}
            </th>
            <th className="py-2 px-4 border-b">
              {t("org_exams.table_status") || "Status"}
            </th>
            <th className="py-2 px-4 border-b">
              {t("org_exams.table_updated") || "Updated"}
            </th>
            <th className="py-2 px-4 border-b">
              {t("org_exams.table_actions") || "Actions"}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td className="py-4 px-4 text-gray-500" colSpan={8}>
                {t("org_exams.empty_state_message") || "No exams"}
              </td>
            </tr>
          ) : (
            rows.map((r) => (
              <tr key={r.id} className="border-b">
                <td className="py-2 px-4 font-medium">{stripHtml(r.title)}</td>
                <td className="py-2 px-4">
                  {r.category}{" "}
                  {r.level && (
                    <span className="text-gray-400">• {r.level}</span>
                  )}
                </td>
                <td className="py-2 px-4">{r.time}</td>
                <td className="py-2 px-4">{r.questions}</td>
                <td className="py-2 px-4">{r.sections}</td>
                <td className="py-2 px-4">
                  <span
                    className={`px-2 py-0.5 rounded-full text-xs font-semibold ${r.statusClass}`}
                  >
                    {r.status}
                  </span>
                </td>
                <td className="py-2 px-4">{r.updatedAt}</td>
                <td className="py-2 px-4">
                  <div
                    className={`flex gap-2 ${
                      isRtl ? "justify-start" : "justify-end"
                    }`}
                  >
                    {/* 🔍 دکمه پیش‌نمایش (همیشه mode=practice) */}
                    <button
                      type="button"
                      onClick={() =>
                        navigate(
                          `/exam/preview/${r.id}?mode=practice`
                        )
                      }
                      className="px-3 py-1.5 rounded-md font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                    >
                      {t("exam_builder.preview") || "پیش‌نمایش"}
                    </button>

                    {/* 🚫 لغو انتشار */}
                    <button
                      disabled={!mayCreate}
                      onClick={() => {
                        if (mayCreate) handleUnpublish(r.id);
                      }}
                      className={`px-3 py-1.5 rounded-md font-medium transition-colors ${
                        mayCreate
                          ? "bg-red-600 text-white hover:bg-red-700"
                          : "bg-gray-300 text-gray-400 cursor-not-allowed"
                      }`}
                    >
                      {t("exam_card.unpublish_button") || "لغو انتشار"}
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
